import { useState, useEffect } from 'react';
import { validateEntry, validateBalance } from '../utils/journalEntryUtils';
import { generateLineItemId } from '../utils/journalEntryUtils';
import {
  JournalEntry,
  JournalEntryStatus,
  RecurringSchedule,
  RenewalInfo,
  ValidationResult,
  AccountingCategorization,
  FiscalCategory,
  LineItem
} from '../types/journalEntry';
import { FundingAllocation, FundType, RestrictionType } from '../types/accounting';

interface UseJournalEntryStateProps {
  initialEntry?: Partial<JournalEntry>;
}

// Add default initial state
const defaultInitialState: Partial<JournalEntry> = {
  date: new Date(),
  description: '',
  type: 'standard',
  status: 'draft' as JournalEntryStatus,
  lineItems: [],
  allocations: [],
  isRecurring: false,
  accountingCategorization: {
    accountCategory: '',
    accountSubcategory: '',
    accountingCode: '',
    functionType: '',
    form990Category: '',
    form990Subcategory: ''
  },
  fiscalCategory: {
    causeId: '',
    campaignId: '',
    fundType: FundType.OPERATING,
    restrictionType: RestrictionType.UNRESTRICTED,
    accountingCode: '',
    taxCategory: '',
    type: 'PROGRAM'
  },
  totalAmount: 0,
  totalDebits: 0,
  totalCredits: 0
};

interface ValidationResults {
  errors: string[];
  warnings: string[];
}

export const useJournalEntryState = ({ initialEntry }: UseJournalEntryStateProps) => {
  // Merge default state with initial entry
  const initialState = {
    ...defaultInitialState,
    ...initialEntry
  };

  // Core form state
  const [formData, setFormData] = useState<Partial<JournalEntry>>(initialState);
  const [lineItems, setLineItems] = useState<LineItem[]>(initialState.lineItems || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBalanced, setIsBalanced] = useState(false);

  // Additional features state
  const [recurringSchedule, setRecurringSchedule] = useState<RecurringSchedule | null>(
    initialState.recurringSchedule || null
  );
  const [renewalInfo, setRenewalInfo] = useState<RenewalInfo | null>(
    initialState.renewalInfo || null
  );
  const [pendingAttachments, setPendingAttachments] = useState<Array<any>>([]);
  const [validationResults, setValidationResults] = useState<ValidationResults>({
    errors: [],
    warnings: []
  });

  // Line item management
  const addLineItem = () => {
    const newItem: LineItem = {
      id: generateLineItemId(),
      accountId: '',
      description: '',
      amount: 0,
      debit: 0,
      credit: 0,
      taxCategory: '',
      allocations: [],
      metadata: {}
    };
    setLineItems([...lineItems, newItem]);
  };

  const removeLineItem = (id: string) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const updateLineItem = (id: string, updates: Partial<LineItem>) => {
    setLineItems(lineItems.map(item => 
      item.id === id ? { ...item, ...updates } : item
    ));
  };

  // Validation
  const validateForm = (): ValidationResult => {
    return validateEntry(formData);
  };

  const clearFormErrors = () => {
    setValidationResults({ errors: [], warnings: [] });
  };

  // Effects
  useEffect(() => {
    // Update balance status whenever line items change
    setIsBalanced(validateBalance(lineItems));
    
    // Update form data with new line items
    setFormData(prev => ({
      ...prev,
      lineItems,
      totalDebits: lineItems.reduce((sum, item) => sum + (item.debit || 0), 0),
      totalCredits: lineItems.reduce((sum, item) => sum + (item.credit || 0), 0)
    }));
  }, [lineItems]);

  useEffect(() => {
    // Update form data when recurring schedule or renewal info changes
    setFormData(prev => ({
      ...prev,
      recurringSchedule,
      renewalInfo
    }));
  }, [recurringSchedule, renewalInfo]);

  useEffect(() => {
    // Validate form data when it changes
    const validation = validateForm();
    setValidationResults({
      errors: validation.errors,
      warnings: [] // Add warning logic if needed
    });
  }, [formData]);

  return {
    // Core form state
    formData,
    setFormData,
    lineItems,
    isSubmitting,
    setIsSubmitting,
    isBalanced,

    // Line item operations
    addLineItem,
    removeLineItem,
    updateLineItem,

    // Additional features
    recurringSchedule,
    setRecurringSchedule,
    renewalInfo,
    setRenewalInfo,
    pendingAttachments,
    setPendingAttachments,

    // Validation
    validationResults,
    clearFormErrors,
    validateForm
  };
};